/**
 * Ticket creator detection.
 *
 * The upstream merchant API does NOT reliably populate `creator`. The canonical
 * signal is `externalCreator`:
 *
 *   - externalCreator populated → merchant created the ticket from the dashboard
 *                                  (the form skips a separate title input, so
 *                                  title and description are typically the same).
 *
 *   - externalCreator null      → admin created the ticket on behalf of the
 *                                  merchant from the trusted/admin side. Title
 *                                  and description are genuinely distinct and
 *                                  the merchant MUST see the description as
 *                                  its own section, plus a "Created on your
 *                                  behalf by Boxy support" badge.
 */

export type TicketCreatorKind = 'merchant-self' | 'admin-on-behalf';

export interface TicketCreatorShape {
  creator?: unknown;
  externalCreator?: unknown;
  title?: string;
  description?: string;
}

const isPresent = (v: unknown): boolean => v !== null && v !== undefined && v !== '';

export function getTicketCreatorKind(ticket: TicketCreatorShape): TicketCreatorKind {
  // externalCreator is the canonical signal. Upstream merchant API doesn't
  // always populate `creator`, so we don't rely on it.
  return isPresent(ticket.externalCreator) ? 'merchant-self' : 'admin-on-behalf';
}

/**
 * Whether the ticket detail view should render `description` as its own section.
 *
 * Rules:
 *   - admin-on-behalf → ALWAYS true (description is a real, distinct message)
 *   - merchant-self with title === description → false (avoid double-rendering)
 *   - merchant-self with differing title/description → true (defensive)
 */
export function shouldShowDescriptionSeparately(ticket: TicketCreatorShape): boolean {
  if (!ticket.description || ticket.description.trim() === '') return false;

  if (getTicketCreatorKind(ticket) === 'admin-on-behalf') return true;

  if (ticket.title && ticket.description && ticket.title !== ticket.description) {
    return true;
  }

  return false;
}
